/**
 * McpCallback — landing page for MCP OAuth redirects.
 *
 * Mounted directly by `main.jsx` when the URL path is `/mcp-callback`
 * (the redirect URI we register dynamically with each MCP server).
 * Same flat-path-routed pattern as the directory-integration
 * `OAuthCallback.jsx`.
 *
 * Reads `?code` + `?state`, calls `exchangeCode` (which reads the
 * stash placed by `startAuthorize`), then writes the resulting
 * tokens into the registry and returns the user to the app shell.
 * If the connect button was opened in a popup we close it and let
 * the originating tab pick up the storage change via the registry
 * subscription; otherwise we just navigate home.
 */

import { useEffect, useState } from "react";
import { exchangeCode, readPendingAuthorize, clearPendingAuthorize } from "../lib/mcp/oauth.js";
import { recordTokens, recordHandshakeError, SERVER_REGISTRY } from "../lib/mcp/registry.js";

// The provider's error code, in words the user can act on. `access_denied`
// is the user declining at the provider's consent screen.
function describeProviderError(serverName, code, description) {
  if (code === "access_denied") {
    return `You did not grant ${serverName} access, so nothing was connected. Connect again when you are ready.`;
  }
  const detail = description || code;
  return `${serverName} sign-in failed (${String(detail).slice(0, 200)}). Try connecting again.`;
}

function isPopupTab() {
  return !!window.opener && window.opener !== window;
}

export default function McpCallback() {
  const [status, setStatus] = useState({ phase: "working", message: "Finishing connection…" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const providerError = params.get("error");
      if (providerError) {
        // Remember the refusal on the pending record so the picker row
        // shows it as failed with the reason, then drop the PKCE stash.
        const pending = readPendingAuthorize();
        const ctx = pending?.ctx;
        const serverName = SERVER_REGISTRY[ctx?.serverId]?.name ?? "The provider";
        const message = describeProviderError(serverName, providerError, params.get("error_description"));
        if (ctx?.tenantId && ctx?.serverId) {
          try { recordHandshakeError(ctx.tenantId, ctx.serverId, message); } catch { /* identity not ready */ }
        }
        clearPendingAuthorize();
        if (!cancelled) setStatus({ phase: "error", message });
        return;
      }
      const code = params.get("code");
      const state = params.get("state");
      if (!code || !state) {
        if (!cancelled) setStatus({ phase: "error", message: "Missing code or state on callback URL." });
        return;
      }
      try {
        const { tokens, ctx } = await exchangeCode({ code, state });
        if (!ctx?.tenantId || !ctx?.serverId) {
          throw new Error("OAuth context missing tenant/server info.");
        }
        recordTokens(ctx.tenantId, ctx.serverId, tokens, { issuer: ctx.issuer });
        const def = SERVER_REGISTRY[ctx.serverId];
        if (cancelled) return;
        setStatus({
          phase: "success",
          message: `Connected to ${def?.name ?? ctx.serverId}.`,
        });
        // Hand control back. Popup mode closes; full-tab mode navigates
        // home so the storage write the registry just emitted is picked
        // up by the chat agent next time it (re)builds.
        if (isPopupTab()) {
          window.setTimeout(() => { try { window.close(); } catch {} }, 600);
        } else {
          window.setTimeout(() => window.location.assign("/"), 700);
        }
      } catch (err) {
        if (!cancelled) {
          setStatus({ phase: "error", message: err?.message ?? "Failed to complete connection." });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const color =
    status.phase === "success" ? "text-[color:var(--green)]" :
    status.phase === "error"   ? "text-red-400" :
    "text-[color:var(--muted)]";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg)]">
      <div className="max-w-md w-full border border-[color:var(--line)] bg-[color:var(--bg)] p-[20px]">
        <h1 className="m-0 text-[13px] font-semibold uppercase tracking-[0.08em] text-[color:var(--ink)] mb-[12px]">
          MCP connection
        </h1>
        <p className={`m-0 text-[12px] ${color}`}>{status.message}</p>
        {status.phase === "error" ? (
          <div className="mt-[16px] flex gap-[6px]">
            {isPopupTab() ? (
              <button
                type="button"
                onClick={() => window.close()}
                className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
              >
                Close tab
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => window.location.assign("/")}
              className="h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[10px] text-[11px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
            >
              Return to dashboard
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
